import { useQuery } from '@tanstack/react-query';

import queryKeys from '@pray/shared/queryKeys';
import api from '@pray/shared/utils/api';
import storage from '@pray/shared/utils/storage';

import type {
  ConversationAnalytics,
  ConversationAnalyticsResponse,
  ConversationPlatform,
  GetConversationAnalyticsParams,
} from './types';

export async function getConversationAnalytics(params: GetConversationAnalyticsParams): Promise<ConversationAnalytics> {
  const { artistId, dateStart, dateEnd, platform } = params;

  if (!artistId) throw Error('Artist ID is required');
  if (!dateStart) throw Error('Start date is required');
  if (!dateEnd) throw Error('End date is required');

  const queryParams: Record<string, string> = {
    date_start: dateStart,
    date_end: dateEnd,
  };

  if (platform && platform.length > 0) {
    queryParams.platform = platform.join(',');
  }

  const url = `/web/studio/artists/${artistId}/chatbot/analytics`;
  const response = await api.executeGetRequest<ConversationAnalyticsResponse>(url, queryParams);

  return response.data.data;
}

export function useConversationAnalytics(
  artistId: string,
  dateStart: string,
  dateEnd: string,
  platform?: ConversationPlatform[]
) {
  const user = storage.getUser();

  return useQuery({
    enabled: !!artistId && !!user?.is_registered && !!dateStart && !!dateEnd,
    queryKey: queryKeys.artistConversationAnalytics(artistId, { dateStart, dateEnd, platform }),
    queryFn: () =>
      getConversationAnalytics({
        artistId,
        dateStart,
        dateEnd,
        platform,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
